import { useEffect, useState } from 'react';
import { ProjectProfileVerifiedBannerVisibleMs } from '../lib/mappingPageHostBridge';

interface UseProjectProfileVerifiedBannerArgs {
  profileConfirmedForCurrentSession: boolean;
  activeProjectSessionKey: string | null;
  readinessStatus: string;
  isBundledDefaultUserProfile: boolean;
  selectedProfileKey: string;
  lastAppliedProfileKey: string;
}

/**
 * Drives the "profile verified for this project" banner: shows once per
 * session/profile combination and auto-hides after
 * ProjectProfileVerifiedBannerVisibleMs.
 */
export const useProjectProfileVerifiedBanner = ({
  profileConfirmedForCurrentSession,
  activeProjectSessionKey,
  readinessStatus,
  isBundledDefaultUserProfile,
  selectedProfileKey,
  lastAppliedProfileKey,
}: UseProjectProfileVerifiedBannerArgs) => {
  const projectProfileVerifiedBannerEligible =
    profileConfirmedForCurrentSession &&
    Boolean(activeProjectSessionKey) &&
    readinessStatus !== 'repairing' &&
    !isBundledDefaultUserProfile;
  const projectProfileVerifiedBannerKey = projectProfileVerifiedBannerEligible
    ? `${activeProjectSessionKey ?? ''}::${selectedProfileKey}::${lastAppliedProfileKey}`
    : null;
  const [visibleProjectProfileVerifiedBannerKey, setVisibleProjectProfileVerifiedBannerKey] =
    useState<string | null>(null);

  useEffect(() => {
    if (!projectProfileVerifiedBannerKey) {
      setVisibleProjectProfileVerifiedBannerKey(null);
      return;
    }

    setVisibleProjectProfileVerifiedBannerKey(projectProfileVerifiedBannerKey);
    const timeoutId = window.setTimeout(() => {
      setVisibleProjectProfileVerifiedBannerKey((currentKey) =>
        currentKey === projectProfileVerifiedBannerKey ? null : currentKey
      );
    }, ProjectProfileVerifiedBannerVisibleMs);

    return () => window.clearTimeout(timeoutId);
  }, [projectProfileVerifiedBannerKey]);

  return (
    Boolean(projectProfileVerifiedBannerKey) &&
    visibleProjectProfileVerifiedBannerKey === projectProfileVerifiedBannerKey
  );
};
